import dayjs from "dayjs";

const UNAVAILABLE_BG = "#edeff0";

const normalizeHora = (value) => String(value || "").slice(0, 5);

const horaParaMinutos = (hora) => {
  const normalizada = normalizeHora(hora);
  if (!normalizada) return null;
  const [h, m] = normalizada.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const buildDateTime = (data, hora) => {
  const dataFormatada = dayjs(data).format("YYYY-MM-DD");
  const horaFormatada = normalizeHora(hora);
  if (!dayjs(dataFormatada).isValid() || !horaFormatada) return null;
  return dayjs(`${dataFormatada}T${horaFormatada}:00`);
};

export const indexarExcecoesPorData = (excecoes = []) => {
  const mapa = new Map();

  const adicionarNoDia = (data, item) => {
    if (!dayjs(data).isValid()) return;
    const chave = dayjs(data).format("YYYY-MM-DD");
    if (!mapa.has(chave)) mapa.set(chave, []);
    mapa.get(chave).push(item);
  };

  (Array.isArray(excecoes) ? excecoes : []).forEach((item) => {
    if (!item) return;

    if (item.tipo === "ferias" && item.data_fim) {
      let cursor = dayjs(item.data).startOf("day");
      const fim = dayjs(item.data_fim).startOf("day");
      if (!cursor.isValid() || !fim.isValid() || fim.isBefore(cursor)) return;

      while (cursor.isBefore(fim) || cursor.isSame(fim, "day")) {
        adicionarNoDia(cursor, item);
        cursor = cursor.add(1, "day");
      }
      return;
    }

    adicionarNoDia(item.data, item);
  });

  return mapa;
};

const resolverContextoDia = (excecoesDoDia = []) => {
  const lista = Array.isArray(excecoesDoDia) ? excecoesDoDia : [];
  const diaFechado = lista.find((item) => item.tipo === "dia_fechado");
  const ferias = lista.find((item) => item.tipo === "ferias");
  const horarioEspecial = lista.find((item) => item.tipo === "horario_especial");
  const bloqueios = lista.filter((item) => item.tipo === "bloqueio_parcial");

  return {
    fechado: Boolean(diaFechado || ferias),
    emFerias: Boolean(ferias),
    motivoFechado: ferias?.motivo || diaFechado?.motivo || "",
    horarioEspecial: horarioEspecial
      ? {
          abertura: normalizeHora(horarioEspecial.hora_inicio),
          fechamento: normalizeHora(horarioEspecial.hora_fim),
          motivo: horarioEspecial.motivo || "",
        }
      : null,
    bloqueios: bloqueios.map((item) => ({
      hora_inicio: normalizeHora(item.hora_inicio),
      hora_fim: normalizeHora(item.hora_fim),
      motivo: item.motivo || "",
    })),
  };
};

const slotCaiEmBloqueio = (data, hora, bloqueios = []) => {
  const slotInicio = horaParaMinutos(hora);
  if (slotInicio === null) return null;

  for (const bloqueio of bloqueios) {
    const inicio = horaParaMinutos(bloqueio.hora_inicio);
    const fim = horaParaMinutos(bloqueio.hora_fim);
    if (inicio === null || fim === null) continue;
    if (slotInicio >= inicio && slotInicio < fim) {
      return bloqueio;
    }
  }

  return null;
};

export const obterBloqueioDoSlot = (dateValue, excecoesPorData, slotMinTime, slotMaxTime) => {
  const slot = dayjs(dateValue);
  if (!slot.isValid()) return null;

  const data = slot.format("YYYY-MM-DD");
  const hora = slot.format("HH:mm");
  const contexto = resolverContextoDia(excecoesPorData.get(data));

  if (contexto.fechado) {
    const motivo = contexto.motivoFechado
      ? `: ${contexto.motivoFechado}`
      : "";
    return {
      message: contexto.emFerias
        ? `Período de férias${motivo}. Não é possível agendar.`
        : `Dia indisponível (folga ou feriado)${motivo}.`,
    };
  }

  if (contexto.horarioEspecial?.abertura && contexto.horarioEspecial?.fechamento) {
    const { abertura, fechamento, motivo } = contexto.horarioEspecial;
    const minutosSlot = horaParaMinutos(hora);
    const minAbertura = horaParaMinutos(abertura);
    const minFechamento = horaParaMinutos(fechamento);

    if (
      minutosSlot !== null &&
      minAbertura !== null &&
      minFechamento !== null &&
      (minutosSlot < minAbertura || minutosSlot >= minFechamento)
    ) {
      const detalhe = motivo ? ` (${motivo})` : "";
      return {
        message: `Fora do horário especial deste dia (${abertura} às ${fechamento})${detalhe}.`,
      };
    }
  }

  const bloqueio = slotCaiEmBloqueio(data, hora, contexto.bloqueios);
  if (bloqueio) {
    const motivo = bloqueio.motivo ? `: ${bloqueio.motivo}` : "";
    return {
      message: `Horário bloqueado (${bloqueio.hora_inicio} às ${bloqueio.hora_fim})${motivo}.`,
    };
  }

  const minSlot = horaParaMinutos(hora);
  const minInicio = horaParaMinutos(slotMinTime);
  const minFim = horaParaMinutos(slotMaxTime);
  if (
    slotMinTime &&
    slotMaxTime &&
    minSlot !== null &&
    minInicio !== null &&
    minFim !== null &&
    (minSlot < minInicio || minSlot >= minFim)
  ) {
    return {
      message: "Fora da faixa exibida no calendário.",
    };
  }

  return null;
};

export const slotDentroHorarioEspecial = (dateValue, excecoesPorData) => {
  const slot = dayjs(dateValue);
  if (!slot.isValid()) return false;

  const data = slot.format("YYYY-MM-DD");
  const hora = slot.format("HH:mm");
  const contexto = resolverContextoDia(excecoesPorData.get(data));

  if (!contexto.horarioEspecial?.abertura || !contexto.horarioEspecial?.fechamento) {
    return false;
  }

  const { abertura, fechamento } = contexto.horarioEspecial;
  return hora >= abertura && hora < fechamento;
};

const criarEventoFundo = ({ start, end, message, id }) => ({
  id: id || `bg-${start}-${end}`,
  start: start?.toISOString?.() || start,
  end: end?.toISOString?.() || end,
  display: "background",
  backgroundColor: UNAVAILABLE_BG,
  borderColor: UNAVAILABLE_BG,
  classNames: ["fc-fz-unavailable-bg"],
  extendedProps: {
    isExcecaoAgenda: true,
    blockReason: message,
  },
});

const criarEventoDiaFechado = (data, motivo, emFerias = false) => {
  const titulo = motivo || (emFerias ? "Férias" : "Dia fechado");
  const mensagem = emFerias
    ? motivo
      ? `Período de férias: ${motivo}`
      : "Período de férias — agendamentos indisponíveis"
    : motivo
      ? `Dia indisponível: ${motivo}`
      : "Dia indisponível (folga ou feriado)";

  return {
    id: `excecao-fechado-${data}`,
    start: data,
    allDay: true,
    title: titulo,
    classNames: [emFerias ? "fc-fz-vacation" : "fc-fz-day-closed"],
    backgroundColor: emFerias ? "#dbeafe" : "#e2e8f0",
    borderColor: emFerias ? "#93c5fd" : "#cbd5e1",
    textColor: emFerias ? "#1e40af" : "#475569",
    extendedProps: {
      isExcecaoAgenda: true,
      tipo: emFerias ? "ferias" : "dia_fechado",
      blockReason: mensagem,
    },
  };
};

export const buildExcecaoCalendarEvents = (
  excecoes = [],
  { slotMinTime = "07:00", slotMaxTime = "22:00" } = {}
) => {
  const allDayEvents = [];
  const backgroundEvents = [];
  const mapa = indexarExcecoesPorData(excecoes);

  mapa.forEach((lista, data) => {
    const contexto = resolverContextoDia(lista);

    if (contexto.fechado) {
      allDayEvents.push(
        criarEventoDiaFechado(data, contexto.motivoFechado, contexto.emFerias)
      );

      const inicio = buildDateTime(data, slotMinTime);
      const fim = buildDateTime(data, slotMaxTime);
      if (inicio && fim?.isAfter(inicio)) {
        const mensagemFundo = contexto.emFerias
          ? contexto.motivoFechado
            ? `Período de férias: ${contexto.motivoFechado}`
            : "Período de férias — agendamentos indisponíveis"
          : contexto.motivoFechado
            ? `Dia indisponível: ${contexto.motivoFechado}`
            : "Dia indisponível (folga ou feriado)";

        backgroundEvents.push(
          criarEventoFundo({
            id: `excecao-fechado-bg-${data}`,
            start: inicio,
            end: fim,
            message: mensagemFundo,
          })
        );
      }
      return;
    }

    if (contexto.horarioEspecial?.abertura && contexto.horarioEspecial?.fechamento) {
      const { abertura, fechamento, motivo } = contexto.horarioEspecial;
      const antesInicio = buildDateTime(data, slotMinTime);
      const antesFim = buildDateTime(data, abertura);
      const depoisInicio = buildDateTime(data, fechamento);
      const depoisFim = buildDateTime(data, slotMaxTime);
      const detalhe = motivo ? ` (${motivo})` : "";

      if (antesInicio && antesFim?.isAfter(antesInicio)) {
        backgroundEvents.push(
          criarEventoFundo({
            id: `excecao-especial-antes-${data}`,
            start: antesInicio,
            end: antesFim,
            message: `Fora do horário especial${detalhe}`,
          })
        );
      }

      if (depoisInicio && depoisFim?.isAfter(depoisInicio)) {
        backgroundEvents.push(
          criarEventoFundo({
            id: `excecao-especial-depois-${data}`,
            start: depoisInicio,
            end: depoisFim,
            message: `Fora do horário especial${detalhe}`,
          })
        );
      }
    }

    contexto.bloqueios.forEach((bloqueio, index) => {
      const inicio = buildDateTime(data, bloqueio.hora_inicio);
      const fim = buildDateTime(data, bloqueio.hora_fim);
      if (!inicio || !fim?.isAfter(inicio)) return;

      const motivo = bloqueio.motivo ? `: ${bloqueio.motivo}` : "";
      backgroundEvents.push(
        criarEventoFundo({
          id: `excecao-bloqueio-${data}-${index}`,
          start: inicio,
          end: fim,
          message: `Horário bloqueado (${bloqueio.hora_inicio} às ${bloqueio.hora_fim})${motivo}`,
        })
      );
    });
  });

  return { allDayEvents, backgroundEvents };
};

/** @deprecated use buildExcecaoCalendarEvents */
export const buildBackgroundEventsFromExcecoes = (excecoes, options) => {
  const { backgroundEvents } = buildExcecaoCalendarEvents(excecoes, options);
  return backgroundEvents;
};

export const listarMesesNoIntervalo = (inicio, fim) => {
  const meses = new Set();
  let cursor = dayjs(inicio).startOf("month");
  const limite = dayjs(fim);

  while (cursor.isBefore(limite) || cursor.isSame(limite, "month")) {
    meses.add(cursor.format("YYYY-MM"));
    cursor = cursor.add(1, "month");
  }

  return [...meses];
};
